'use client';

import { useQuery } from '@tanstack/react-query';
import { useEffect, useMemo, useState } from 'react';

import { getCurrentOfferingsTombstones } from 'services/explore';
import { testSlugs } from 'utils/helpers';

const testSlugsSet = new Set(testSlugs);

// PD-35989 — defensive guard against stage test fixtures that are not in the
// hardcoded `testSlugs` list (e.g. "...for reg cf automation DO NOT TOUCH").
// Real offerings never carry these markers, so dropping any offering whose
// name/company name matches keeps obvious QA fixtures out of the homepage
// shelves and the featured spotlight. On production these fixtures do not
// exist, so this only affects non-prod seed data.
const TEST_NAME_PATTERN =
  /do not touch|qa test|test offering|automation test|for reg cf automation/i;
const isTestFixture = (offering) => {
  if (!offering) return false;
  if (testSlugsSet.has(offering.link)) return true;
  // Check both fields independently — a fixture can have a normal `name` but a
  // matching `company_name` (or vice-versa), so `name || company_name` would
  // skip the second field and let it slip through.
  return (
    TEST_NAME_PATTERN.test(offering.name || '') ||
    TEST_NAME_PATTERN.test(offering.company_name || '')
  );
};

const filterOfferings = (offerings) => offerings?.filter((offering) => !isTestFixture(offering));

const isEmptyResponse = (data) =>
  !data ||
  (!data.most_momentum?.length &&
    !data.most_popular?.length &&
    !data.newest?.length &&
    !data.closing_soon?.length);

const MAX_EMPTY_RETRIES = 3;

const useGetCurrentOfferingsTombstones = ({ enabled = true } = {}) => {
  const [fingerprint, setFingerprint] = useState(
    typeof window !== 'undefined' ? window.fingerprint || null : null,
  );

  useEffect(() => {
    if (!enabled) return;
    if (fingerprint || typeof window === 'undefined') return;

    let cancelled = false;
    let idleId = null;

    const check = () => {
      if (cancelled) return;
      if (window.fingerprint) {
        setFingerprint(window.fingerprint);
      } else {
        const schedule = window.requestIdleCallback || ((cb) => setTimeout(cb, 200));
        idleId = schedule(check, { timeout: 2000 });
      }
    };

    const schedule = window.requestIdleCallback || ((cb) => setTimeout(cb, 200));
    idleId = schedule(check, { timeout: 2000 });

    return () => {
      cancelled = true;
      const cancel = window.cancelIdleCallback || clearTimeout;
      if (idleId != null) cancel(idleId);
    };
  }, [enabled, fingerprint]);

  const queryKey = useMemo(() => ['currentHomepageOfferings', fingerprint], [fingerprint]);

  const { isLoading, data, error } = useQuery({
    queryKey,
    queryFn: async () => {
      const data = await getCurrentOfferingsTombstones({ fingerprint });
      data.most_momentum = filterOfferings(data.most_momentum);
      data.most_popular = filterOfferings(data.most_popular);
      data.newest = filterOfferings(data.newest);
      data.closing_soon = filterOfferings(data.closing_soon);
      // PD-35981 — SE3 A/B ranking rows (primary-facade PR #1195) are additive
      // on the same response. Apply the same test-slug filtering for parity;
      // they pass through `transformPrimaryOfferings` unchanged (any array key
      // is mapped) and are consumed by the SE3 adapter only.
      if (data.most_momentum_se3) data.most_momentum_se3 = filterOfferings(data.most_momentum_se3);
      if (data.closing_soon_se3) data.closing_soon_se3 = filterOfferings(data.closing_soon_se3);

      if (isEmptyResponse(data)) {
        throw new Error('Tombstones endpoint returned empty data');
      }

      return data;
    },
    retry: (failureCount, err) => {
      if (err?.message === 'Tombstones endpoint returned empty data') {
        return failureCount < MAX_EMPTY_RETRIES;
      }
      return failureCount < 2;
    },
    retryDelay: (attempt) => Math.min(2000 * 2 ** attempt, 10000),
    refetchOnMount: false,
    enabled,
  });

  return {
    data,
    error,
    isLoading,
  };
};

export default useGetCurrentOfferingsTombstones;
